import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { ImageResolution } from 'helpers';
import { UserService, WindowService } from 'services';
import { ProductService } from 'app/content/services/product.service';
import { DownloadsService } from 'app/content/services/downloads.service';
import { DownloadModel } from 'app/content/models/download.model';
import { DownloadContentService, DownloadEventType } from 'app/content/services/download-content.service';
import { VideoDeliveryFeatureService } from 'app/content/services/video-delivery-feature.service';
import {
    DownloadSelection,
    DownloadSelectionContext,
    DownloadSelectionMetadata,
    DEFAULT_DOWNLOAD_SELECTION
} from './download-selection.types';

@Injectable({ providedIn: 'root' })
export class DownloadSelectionService {
    private pendingContext: DownloadSelectionContext | null = null;
    private readonly videoDeliveryFeatureService = inject(VideoDeliveryFeatureService);

    constructor(
        private productService: ProductService,
        private downloadsService: DownloadsService,
        private downloadModel: DownloadModel,
        private downloadContentService: DownloadContentService,
        private userService: UserService,
        private windowService: WindowService
    ) {}

    public setPendingContext(context: DownloadSelectionContext) {
        this.pendingContext = context;
    }

    public getPendingContext(): DownloadSelectionContext | null {
        return this.pendingContext;
    }

    public clearPendingContext() {
        this.pendingContext = null;
    }

    public getDownloadMetadata(): DownloadSelectionMetadata | null {
        if (this.pendingContext?.type !== 'summaryBar') {
            return null;
        }

        const { imageCount, videoCount } = this.downloadsService.getDownloadListMediaCounts();

        return {
            imageCount,
            videoCount
        };
    }

    public executeDownload(selection: DownloadSelection): Observable<{ downloadUrl?: string }> | Promise<void> {
        const context = this.pendingContext;
        if (!context) {
            return Promise.resolve();
        }

        const normalizedSelection = this.normalizeVideoSelection(selection);

        let result: Observable<{ downloadUrl?: string }> | Promise<void>;

        switch (context.type) {
            case 'bulk':
                result = this.executeBulkDownload(context, normalizedSelection);
                break;
            case 'order':
                result = this.executeOrderDownload(context, normalizedSelection);
                break;
            case 'summaryBar':
                result = this.executeSummaryBarDownload(normalizedSelection);
                break;
            case 'lightbox':
                result = this.executeLightboxDownload(context, normalizedSelection);
                break;
            default:
                result = Promise.resolve();
        }

        this.clearPendingContext();
        return result;
    }

    public getDefaultSelection(overrides: Partial<DownloadSelection> = {}): DownloadSelection {
        return {
            ...DEFAULT_DOWNLOAD_SELECTION,
            ...overrides
        };
    }

    public isDownloadEnabled(selection: DownloadSelection): boolean {
        return selection.images || selection.video || selection.productAttributes;
    }

    public isSubOptionSelectionValid(selection: DownloadSelection): boolean {
        const imagesValid = !selection.images || !!selection.resolution;
        const csvValid = !selection.productAttributes || !!selection.csvLanguage;
        return imagesValid && csvValid;
    }

    private executeBulkDownload(
        context: Extract<DownloadSelectionContext, { type: 'bulk' }>,
        selection: DownloadSelection
    ): Observable<{ downloadUrl?: string }> {
        const baseParams = {
            ...(context.gtins ? { gtins: context.gtins } : {}),
            ...(context.articleNumbers ? { articleNumbers: context.articleNumbers } : {}),
            initiator: context.initiator,
            images: selection.images,
            video: selection.video
        };

        const hasMedia = selection.images || selection.video;
        const hasCsv = selection.productAttributes;

        if (hasMedia && hasCsv) {
            return this.productService.bulkImagesDownloadWithCsv({
                ...baseParams,
                userLanguage: this.userService.getPreferredLanguage(),
                size: selection.resolution,
                csvLanguage: selection.csvLanguage
            });
        }

        if (hasMedia) {
            return this.productService.bulkImagesDownload({
                ...baseParams,
                size: selection.resolution
            });
        }

        return this.productService.bulkDownload({
            ...baseParams,
            userLanguage: this.userService.getPreferredLanguage(),
            size: selection.resolution || ImageResolution.Original,
            csvLanguage: selection.csvLanguage
        });
    }

    private executeOrderDownload(
        context: Extract<DownloadSelectionContext, { type: 'order' }>,
        selection: DownloadSelection
    ): Observable<any> {
        const downloadType = this.resolveDownloadEventType(selection);
        const downloadObservable = this.downloadContentService.downloadContent(
            downloadType,
            { orderId: context.orderId, orderType: context.orderType },
            context.initiator,
            selection.resolution,
            selection.csvLanguage,
            selection.images,
            selection.video
        );

        this.downloadContentService.handleDownload(downloadObservable);
        return downloadObservable;
    }

    private executeSummaryBarDownload(selection: DownloadSelection): Promise<void> {
        const hasMedia = selection.images || selection.video;
        const hasCsv = selection.productAttributes;
        const size = selection.resolution || ImageResolution.Original;

        if (hasMedia && hasCsv) {
            return this.downloadsService
                .downloadImagesAndProductData(selection.images, selection.video, size, selection.csvLanguage)
                .then(() => undefined);
        }

        if (hasMedia) {
            return this.downloadsService.downloadImages(selection.images, selection.video, size).then(() => undefined);
        }

        if (hasCsv) {
            return this.downloadsService.downloadProductData(selection.csvLanguage).then(() => undefined);
        }

        return Promise.resolve();
    }

    private async executeLightboxDownload(
        context: Extract<DownloadSelectionContext, { type: 'lightbox' }>,
        selection: DownloadSelection
    ): Promise<void> {
        if (!selection.images && !selection.video) {
            return;
        }

        const downloadUrl = await this.downloadModel.getDownloadUrl(
            {
                productImages: [{ gtin: context.gtin, fileId: context.fileId }],
                initiator: context.initiator,
                images: selection.images,
                video: selection.video
            },
            selection.resolution || ImageResolution.Original
        );

        this.windowService.location = downloadUrl;
    }

    private resolveDownloadEventType(selection: DownloadSelection): DownloadEventType {
        const hasMedia = selection.images || selection.video;

        if (hasMedia && selection.productAttributes) {
            return DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV;
        }

        if (hasMedia) {
            return DownloadEventType.DOWNLOAD_IMAGES;
        }

        return DownloadEventType.DOWNLOAD_CSV;
    }

    private normalizeVideoSelection(selection: DownloadSelection): DownloadSelection {
        if (this.videoDeliveryFeatureService.enabled()) {
            return selection;
        }

        return {
            ...selection,
            video: false
        };
    }
}
